import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonText } from "@repo/components/src/ui/button";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Image } from "@repo/components/src/ui/image";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import React, { ComponentProps } from "react";
import { Linking } from "react-native";

export type IntegrationCta = {
  label: string;
  href?: string;
  onPress?: () => void;
  disabled?: boolean;
};

type LogoSource = ComponentProps<typeof Image>["source"];

type Props = {
  /** Optional small uppercase pill above the heading */
  eyebrow?: string;
  heading: string;
  description: string;
  /** Pastel CSS gradient for the card background */
  gradient: string;
  /** One or two CTAs */
  ctas: [IntegrationCta] | [IntegrationCta, IntegrationCta];
  /** Logos rendered in the right-side illustration area */
  logos: LogoSource[];
};

const triggerCta = (cta: IntegrationCta) => {
  if (cta.disabled) return;
  if (cta.onPress) return cta.onPress();
  if (!cta.href) return;
  if (typeof window !== "undefined") {
    window.open(cta.href, cta.href.startsWith("http") ? "_blank" : "_self");
    return;
  }
  Linking.openURL(cta.href);
};

const IntegrationCard = React.memo(
  ({ eyebrow, heading, description, gradient, ctas, logos }: Props) => {
    return (
      <Box
        className="relative rounded-2xl overflow-hidden border border-outline-100"
        style={{ background: gradient } as object}
      >
        <HStack className="items-stretch">
          <VStack space="md" className="flex-1 px-7 py-7 z-10">
            {eyebrow ? (
              <Box className="self-start bg-brand-100 px-3 py-1 rounded-full">
                <Text
                  size="2xs"
                  className="text-brand-700 font-bold uppercase tracking-wide"
                >
                  {eyebrow}
                </Text>
              </Box>
            ) : null}
            <Heading size="lg" className="text-typography-900 max-w-xl">
              {heading}
            </Heading>
            <Text size="sm" className="text-typography-600 max-w-xl">
              {description}
            </Text>
            <HStack space="sm" className="mt-2 flex-wrap">
              {ctas.map((cta) => (
                <Button
                  key={cta.label}
                  size="md"
                  rounded="lg"
                  variant="outline"
                  action="brand"
                  onPress={() => triggerCta(cta)}
                  isDisabled={cta.disabled}
                >
                  <ButtonText>{cta.label}</ButtonText>
                </Button>
              ))}
            </HStack>
          </VStack>
          <Box
            className="hidden md:flex relative items-center justify-center pr-10"
            style={{ minWidth: 280 }}
          >
            <Box
              className="absolute"
              style={
                {
                  width: 260,
                  height: 260,
                  borderRadius: 999,
                  background:
                    "radial-gradient(circle at center, rgba(255,255,255,0.85), transparent 65%)",
                } as object
              }
            />
            <HStack space="sm" className="relative items-center">
              {logos.map((logo, i) => (
                <Box
                  key={i}
                  className={
                    logos.length > 1 && i === 0
                      ? "w-24 h-24 rounded-2xl overflow-hidden -mr-3 z-10 bg-background-0"
                      : "w-24 h-24 rounded-2xl overflow-hidden bg-background-0"
                  }
                >
                  <Image
                    source={logo}
                    alt={`logo-${i}`}
                    size="none"
                    className="w-full h-full"
                    contentFit="cover"
                  />
                </Box>
              ))}
            </HStack>
          </Box>
        </HStack>
      </Box>
    );
  },
);

IntegrationCard.displayName = "IntegrationCard";

export default IntegrationCard;
